import type { MessageCenterRow, TopicSource } from 'src/models/chat';
import { formatClientClock, shortZoneLabel } from '../quote/shiftTimePresentation';
import { eventThreadIds } from './eventThreadLinks';

export function messagePreview(content: string, type: string) {
  if (type === 'file') return 'File Attached';
  if (type === 'audio') return 'Audio Message';
  if (type === 'image') return 'Image Attached';
  return /^chat-attachments\//.test(content) ? 'Attachment' : content;
}
export function category(row: MessageCenterRow) {
  if (row.topic.source?.notificationId) return 'Update Requests';
  if (row.topic.source?.type === 'invoice') return 'Invoices';
  if (row.topic.shift) return 'Shift Questions';
  return row.chat.Event ? 'Event Questions' : 'General';
}
export const topicLabel = (row: MessageCenterRow) => row.topic.displayLabel || row.topic.label;
export function workerName(row: MessageCenterRow, self?: number) {
  const workerId = eventThreadIds(row.chat)?.workerId;
  return (row.chat.participants ?? []).filter(p => workerId ? p.userId === workerId : p.userId !== self)
    .map(p => `${p.user?.firstName ?? ''} ${p.user?.lastName ?? ''}`.trim()).filter(Boolean).join(', ') || 'Company Conversation';
}
export function shiftLabel(row: MessageCenterRow) {
  const shift = row.topic.shift;
  if (!shift) return category(row);
  let suffix = shift.startDate;
  try {
    suffix = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...(shift.startsAt ? { hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' } : {}), timeZone: shift.timezone || 'UTC' }).format(new Date(shift.startsAt || shift.startDate));
  } catch { /* Preserve source date when it cannot be formatted. */ }
  const clocks = !shift.startsAt && shift.startTime ? ` · ${formatClientClock(shift.startTime)} - ${formatClientClock(shift.endTime || '')} ${shortZoneLabel(shift.timezone, shift.startDate)}` : '';
  return `${shift.position} · ${suffix}${clocks}`;
}
export function sourceAction(source?: TopicSource | null) {
  if (source?.type === 'booking') return 'View Booking';
  if (source?.type === 'callTime') return 'View Shift';
  if (source?.type === 'invoice') return 'View Invoice';
  if (source?.type === 'notification') return 'View Request';
  if (source?.type === 'event') return 'View Event';
  return 'Open Source';
}
export function activityTime(value?: string, full = false) {
  const date = new Date(value || '');
  if (!Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { ...(full ? { year: 'numeric' as const } : {}), month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' }).format(date);
}
export const eventKey = (row: MessageCenterRow) => String(row.chat.Event?.id ?? 'general');
export const shiftKey = (row: MessageCenterRow) => `${eventKey(row)}:${row.topic.shift?.id ?? category(row)}`;
const activity = (row: MessageCenterRow) => new Date(row.topic.lastMessageAt).getTime() || row.topic.lastMessage.id;
function upcoming(row: MessageCenterRow, now: number) {
  const time = new Date(row.topic.shift?.startsAt || row.topic.shift?.startDate || '').getTime();
  return time >= now ? time : Infinity;
}
/** MSG-03: sort whole groups, then their topics, so no event fragments into repeated bands. */
export function sortThreads(rows: MessageCenterRow[], sort: string, now = Date.now()) {
  const eventRank = new Map<string, number>(), shiftRank = new Map<string, number>();
  for (const row of rows) {
    const score = sort === 'upcoming' ? upcoming(row, now) : -activity(row);
    for (const [map, key] of [[eventRank, eventKey(row)], [shiftRank, shiftKey(row)]] as const) map.set(key, Math.min(map.get(key) ?? Infinity, score));
  }
  const compare = (a: number, b: number) => a === b ? 0 : a < b ? -1 : 1;
  return [...rows].sort((a,b) => compare(eventRank.get(eventKey(a))!, eventRank.get(eventKey(b))!) || eventKey(a).localeCompare(eventKey(b)) || compare(shiftRank.get(shiftKey(a))!, shiftRank.get(shiftKey(b))!) || shiftKey(a).localeCompare(shiftKey(b)) || activity(b)-activity(a) || b.topic.lastMessage.id-a.topic.lastMessage.id);
}
